/**
 * The builder's widget picker with the client's KPIs in its KPIs tab, as in
 * AgencyAnalytics. KPIs are loaded when the picker opens; if they cannot be,
 * the tab is simply empty and the rest of the picker still works.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { Inserter, type InserterItem } from '../reports/Inserter.tsx';
import type { WidgetSeed } from '../reports/new-widget.ts';
import type { Catalogue } from '../reports/reports-api.ts';
import { seedForKpi } from './kpi-widget.ts';
import { kpisApi } from './kpis-api.ts';

export function KpiInserter(props: {
  organisationId: string;
  clientId: string | null;
  catalogue: Catalogue;
  onPick: (seed: WidgetSeed) => void;
  onClose: () => void;
}): ReactNode {
  const [items, setItems] = useState<InserterItem[]>([]);
  const { organisationId, clientId } = props;
  useEffect(() => {
    if (!clientId) return;
    kpisApi
      .list(organisationId, clientId)
      .then((kpis) =>
        setItems(
          kpis.map((kpi) => ({
            id: `kpi.${kpi.id}`,
            label: kpi.title,
            hint: kpi.target_type
              ? `${kpi.title}: progress towards its target, as a goal bar.`
              : `${kpi.title}, as its saved chart.`,
            seed: seedForKpi(kpi),
            glyph: kpi.target_type ? 'goal' : 'metric',
          })),
        ),
      )
      .catch(() => setItems([]));
  }, [organisationId, clientId]);
  return (
    <Inserter
      catalogue={props.catalogue}
      kpis={items}
      onPick={props.onPick}
      onClose={props.onClose}
    />
  );
}
